import { gql } from '@apollo/client'
import dynamic from 'next/dynamic'

import Advertisement, { AdPage, AdType, PlaceSectionForAd } from '@web/components/Advertisement/Advertisement'
import LANBanner from '@web/components/LANBanner/LANBanner'
import { LANExposureArea } from '@web/components/LANBanner/LANBanner.constant'
import Loading from '@web/components/Loading'
import GlobalNoContent from '@web/components/NoContent'
import { NoContentTypes } from '@web/components/NoContent/type'
import PlaceSection from '@web/components/PlaceSection/PlaceSection'
import { ReviewDetail_ReviewGroupFragment, ReviewGroupExposeStatus } from '@web/gql/graphql'
import useAppBgColor, { AppBgColors } from '@web/hooks/useAppBgColor'
import useMe from '@web/hooks/useMe'
import { useIsMyPageViewLogging } from '@web/hooks/usePageViewLogging'
import { useProfileMy } from '@web/hooks/useProfileMy'
import useScroll from '@web/hooks/useScroll'
import { messages } from '@web/libs/constants'
import nClicks from '@web/libs/nlog/click/codes'
import Place from '@web/pages/visit/components/VisitDetail/Place/Place'
import { Header } from '@web/ui/components/Header/Simple'

import styles from './index.module.scss'
import ReviewContent from './ReviewContent/ReviewContent'

const DynamicPlaceOtherReviewList = dynamic(() => import('./ReviewContent/PlaceOtherReviewList'), {
  loading: () => <Loading height={200} />,
})
interface Props {
  reviewGroup: ReviewDetail_ReviewGroupFragment
}

const HEADER_HEIGHT = 70

const ReviewDetail = ({ reviewGroup }: Props) => {
  useAppBgColor(AppBgColors.GRAY)
  const { me, loading: meLoading } = useMe()
  const showHeaderTitle = useScroll((_, y) => y > HEADER_HEIGHT)
  const { showProfileMy, handleClickBackButton, handleClickProfileMyButton, profileImageUrl, borderImageUrl } =
    useProfileMy()
  const isMine = me?.id === reviewGroup.author?.id
  const adProfitShareUserIdno = reviewGroup?.author?.idno ?? undefined
  const showAd = !meLoading && reviewGroup.exposeStatus === ReviewGroupExposeStatus.Expose

  useIsMyPageViewLogging({ isMine })

  if (
    [ReviewGroupExposeStatus.UnexposePermanently, ReviewGroupExposeStatus.UnexposeTemporarily].includes(
      reviewGroup.exposeStatus,
    )
  ) {
    return <NoContent exposeStatus={reviewGroup.exposeStatus} isMine={isMine} />
  }

  const placeName = reviewGroup.place?.name ?? reviewGroup.visit?.displayName ?? messages.NONE_PLACE_ADDRESS

  return (
    <div className="place_simple_wrapper_new">
      <Header
        title={showHeaderTitle ? placeName : undefined}
        onPrevClick={handleClickBackButton}
        onClickProfileMy={handleClickProfileMyButton}
        showMy={!showHeaderTitle && showProfileMy}
        clickCode={{ back: nClicks.reviewEnd.BACK, my: nClicks.reviewEnd.PLACEMY }}
        profileImageUrl={profileImageUrl}
        borderImageUrl={borderImageUrl}
      />
      {/* 장소 정보 섹션 */}
      {reviewGroup.place && (
        <Place
          showHeaderTitle={showHeaderTitle}
          place={reviewGroup.place}
          displayName={placeName}
          clickCodes={{
            place: nClicks.reviewEnd.PLACE,
            save: nClicks.reviewEnd.SAVE,
            unsave: nClicks.reviewEnd.UNSAVE,
          }}
        />
      )}
      {/* 광고 섹션 */}
      {showAd && !meLoading && (
        <PlaceSectionForAd isSmartChannel>
          <Advertisement
            adSlotElementId="review_detail_top_ad"
            type={AdType.SMART_CHANNEL}
            page={isMine ? AdPage.REVIEW_END_MINE : AdPage.REVIEW_END_OTHERS}
            userIdno={adProfitShareUserIdno}
          />
        </PlaceSectionForAd>
      )}
      {/* 내 리뷰 섹션 */}
      <PlaceSection>
        <ReviewContent reviewGroup={reviewGroup} />
      </PlaceSection>
      {/* LAN 배너 */}
      <LANBanner
        exposureArea={LANExposureArea.REVIEW_END_BANNER}
        nClickCodes={{ light: nClicks.lan.REVIEW_END_BANNER, dark: nClicks.lan.REVIEW_END_BANNER_DARK }}
      />
      {/* 이 장소의 다른 리뷰 섹션 */}
      {reviewGroup.exposeStatus === ReviewGroupExposeStatus.Expose && reviewGroup.place?.id && (
        <PlaceSection className={styles.other_review_section}>
          <DynamicPlaceOtherReviewList reviewGroup={reviewGroup} isMine={isMine} />
        </PlaceSection>
      )}
    </div>
  )
}

const NoContent = ({ exposeStatus, isMine }: { exposeStatus: ReviewGroupExposeStatus; isMine: boolean }) => {
  const { showProfileMy, handleClickBackButton, handleClickProfileMyButton, profileImageUrl, borderImageUrl } =
    useProfileMy()

  const type =
    exposeStatus === ReviewGroupExposeStatus.UnexposeTemporarily
      ? NoContentTypes.TEMPORARY_SUSPENSION_NOTICE
      : NoContentTypes.PERMANENT_SUSPENSION_NOTICE
  return (
    <div className="place_simple_wrapper_new">
      <Header
        onPrevClick={handleClickBackButton}
        onClickProfileMy={handleClickProfileMyButton}
        showMy={showProfileMy}
        clickCode={{ back: nClicks.reviewEnd.BACK, my: nClicks.reviewEnd.PLACEMY }}
        profileImageUrl={profileImageUrl}
        borderImageUrl={borderImageUrl}
      />
      <GlobalNoContent type={type} isMine={isMine} />
    </div>
  )
}

ReviewDetail.NoContent = NoContent
ReviewDetail.fragments = {
  reviewGroup: gql(`
    fragment ReviewDetail_ReviewGroup on ReviewGroup {
      id
      selectedProof {
        id
        type
      }
      visit {
        id
        displayName
      }
      place {
        ...VisitDetail_Place
        ...UseGoToPlaceDetail_Place
      }
      ...PlaceOtherReviewList_ReviewGroup
      ...ReviewContent_ReviewGroup
      
    }
  `),
}

export default ReviewDetail
